import React, { useState, useEffect } from 'react';
import '../styles/Settings.css';
import '../styles/BasicColors.css';

const colorPalettes = [
    {
        name: 'Flower Shop',
        colors: ['#FFF5F7', '#FFFFFF', '#D53F8C', '#FFFFFF', '#2D3748'],
        preview: { bg: '#FFF5F7', header: '#D53F8C', accent: '#ED64A6', text: '#2D3748' }
    },
    {
        name: 'Gaming',
        colors: ['#F5F3FF', '#FFFFFF', '#5B21B6', '#FFFFFF', '#1F2937'],
        preview: { bg: '#F5F3FF', header: '#5B21B6', accent: '#8B5CF6', text: '#1F2937' }
    },
    {
        name: 'Corporate',
        colors: ['#F4F6F8', '#FFFFFF', '#1E3A5F', '#FFFFFF', '#1F2937'],
        preview: { bg: '#F4F6F8', header: '#1E3A5F', accent: '#3B82F6', text: '#1F2937' }
    },
    {
        name: 'Restaurant',
        colors: ['#FFF8E7', '#FFFFFF', '#C62828', '#FFFFFF', '#1F2937'],
        preview: { bg: '#FFF8E7', header: '#C62828', accent: '#EF5350', text: '#1F2937' }
    },
    {
        name: 'Wellness',
        colors: ['#E0F2F1', '#FFFFFF', '#00796B', '#FFFFFF', '#1F2937'],
        preview: { bg: '#E0F2F1', header: '#00796B', accent: '#26A69A', text: '#1F2937' }
    },
    {
        name: 'Nature',
        colors: ['#EFEBE9', '#FFFFFF', '#5D4037', '#FFFFFF', '#1F2937'],
        preview: { bg: '#EFEBE9', header: '#5D4037', accent: '#8D6E63', text: '#1F2937' }
    },
    {
        name: 'Technology',
        colors: ['#E3F2FD', '#FFFFFF', '#0D47A1', '#FFFFFF', '#1F2937'],
        preview: { bg: '#E3F2FD', header: '#0D47A1', accent: '#2196F3', text: '#1F2937' }
    },
    {
        name: 'Finance',
        colors: ['#E8EAF6', '#FFFFFF', '#1A237E', '#FFFFFF', '#1F2937'],
        preview: { bg: '#E8EAF6', header: '#1A237E', accent: '#3F51B5', text: '#1F2937' }
    },
    {
        name: 'Healthcare',
        colors: ['#E3F2FD', '#FFFFFF', '#1565C0', '#FFFFFF', '#1F2937'],
        preview: { bg: '#E3F2FD', header: '#1565C0', accent: '#42A5F5', text: '#1F2937' }
    },
    {
        name: 'Education',
        colors: ['#FFF8E1', '#FFFFFF', '#E65100', '#FFFFFF', '#1F2937'],
        preview: { bg: '#FFF8E1', header: '#E65100', accent: '#FF9800', text: '#1F2937' }
    },
    {
        name: 'Fashion',
        colors: ['#FAFAFA', '#FFFFFF', '#212121', '#FFFFFF', '#1F2937'],
        preview: { bg: '#FAFAFA', header: '#212121', accent: '#616161', text: '#1F2937' }
    },
    {
        name: 'Food & Bev',
        colors: ['#FBE9E7', '#FFFFFF', '#BF360C', '#FFFFFF', '#1F2937'],
        preview: { bg: '#FBE9E7', header: '#BF360C', accent: '#FF5722', text: '#1F2937' }
    },
    {
        name: 'Travel',
        colors: ['#E0F7FA', '#FFFFFF', '#006064', '#FFFFFF', '#1F2937'],
        preview: { bg: '#E0F7FA', header: '#006064', accent: '#00ACC1', text: '#1F2937' }
    },
    {
        name: 'Real Estate',
        colors: ['#ECEFF1', '#FFFFFF', '#37474F', '#FFFFFF', '#1F2937'],
        preview: { bg: '#ECEFF1', header: '#37474F', accent: '#78909C', text: '#1F2937' }
    },
    {
        name: 'Entertainment',
        colors: ['#FCE4EC', '#FFFFFF', '#880E4F', '#FFFFFF', '#1F2937'],
        preview: { bg: '#FCE4EC', header: '#880E4F', accent: '#E91E63', text: '#1F2937' }
    },
    {
        name: 'Automotive',
        colors: ['#ECEFF1', '#FFFFFF', '#263238', '#FFFFFF', '#1F2937'],
        preview: { bg: '#ECEFF1', header: '#263238', accent: '#B71C1C', text: '#1F2937' }
    },
    {
        name: 'Kids',
        colors: ['#FFF3E0', '#FFFFFF', '#F57C00', '#FFFFFF', '#1F2937'],
        preview: { bg: '#FFF3E0', header: '#F57C00', accent: '#FFAB40', text: '#1F2937' }
    },
    {
        name: 'Eco/Green',
        colors: ['#E8F5E9', '#FFFFFF', '#2E7D32', '#FFFFFF', '#1F2937'],
        preview: { bg: '#E8F5E9', header: '#2E7D32', accent: '#66BB6A', text: '#1F2937' }
    }
];

function BasicColors({ loader, onPaletteSelect, onSave, selectedPalette }) {
    const [selected, setSelected] = useState(null);

    // When selectedPalette changes (from saved theme), find and select it
    useEffect(() => {
        if (selectedPalette && selectedPalette.name) {
            const palette = colorPalettes.find(p => p.name === selectedPalette.name);
            if (palette) {
                setSelected(palette);
            }
        }
    }, [selectedPalette]);

    const handleSelect = (palette) => {
        setSelected(palette);
        onPaletteSelect(palette);
    };

    const handleSaveClick = () => {
        if (selected) {
            // Use the header color as the basket background for theme consistency
            const headerColor = selected.colors[2] || '#1976d2';
            const accentColor = selected.preview?.accent || headerColor;
            const fontColor = selected.colors[4] || '#1F2937';
            
            const colorsToSave = {
                // Background colors
                dashboardBackgroundColor: selected.colors[0] || '#F5F5F5',
                tileBackgroundColor: selected.colors[1] || '#FFFFFF',
                headerBackgroundColor: headerColor,
                dashboardHeaderColor: selected.colors[3] || '#FFFFFF',
                
                // Text colors
                fontColor: fontColor,
                categoryButtonTextColor: headerColor,
                productDescriptionColor: '#6B7280',
                priceColor: headerColor,
                buttonTextColor: '#FFFFFF',
                
                // Basket colors
                basketBackgroundColor: headerColor,
                basketBackgroundColorInnerCircle: accentColor,
                basketIconColor: '#FFFFFF',
                basketCountBackgroundColor: '#E53E3E',
                basketCountNumberColor: '#FFFFFF',
                
                // Theme name
                selectedTheme: selected.name
            };
            onSave(colorsToSave);
        }
    };

    return (
        <div className="form-wrapper" style={{ opacity: loader ? 0.3 : 1 }}>
            <div className="color-palettes-container">
                {colorPalettes.map((palette) => (
                    <div
                        key={palette.name}
                        className={`palette-tile ${selected && selected.name === palette.name ? 'selected' : ''}`}
                        onClick={() => handleSelect(palette)}
                    >
                        <h3>{palette.name}</h3>
                        <div className="palette-preview" style={{ backgroundColor: palette.preview.bg }}>
                            <div className="preview-header" style={{ backgroundColor: palette.preview.header }}>
                                <span style={{ color: palette.preview.bg === palette.preview.header ? '#fff' : '#fff' }}>Header</span>
                            </div>
                            <div className="preview-content">
                                <div className="preview-card" style={{ backgroundColor: '#fff', borderLeft: `3px solid ${palette.preview.accent}` }}>
                                    <div className="preview-text" style={{ backgroundColor: palette.preview.text, opacity: 0.8 }}></div>
                                    <div className="preview-text short" style={{ backgroundColor: palette.preview.text, opacity: 0.5 }}></div>
                                </div>
                                <div className="preview-card" style={{ backgroundColor: '#fff', borderLeft: `3px solid ${palette.preview.accent}` }}>
                                    <div className="preview-text" style={{ backgroundColor: palette.preview.text, opacity: 0.8 }}></div>
                                    <div className="preview-text short" style={{ backgroundColor: palette.preview.text, opacity: 0.5 }}></div>
                                </div>
                            </div>
                        </div>
                        <div className="color-swatches">
                            {palette.colors.slice(0, 5).map((color, index) => (
                                <div
                                    key={`${color}-${index}`}
                                    className="color-swatch"
                                    style={{ backgroundColor: color }}
                                    title={color}
                                ></div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className='form-footer'>
                <button
                    type="button"
                    disabled={!selected}
                    className="submit-button"
                    onClick={handleSaveClick}
                >
                    Save
                </button>
            </div>
        </div>
    );
}

export default BasicColors;
